import { useState, useEffect } from "react";
import { Form, Input, Button, Card, Row, Col, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import signup from "@/projects/Civi_Circle_Admin/assets/signup.png";
import { useUser } from "@/context/useUser";
import { getTenantId } from "@/lib/storeManager";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import navigationRoutes from "@/utils/navigationRoutes";
import { ZulipService } from "@/services/APIs";

const { Title, Text, Paragraph } = Typography;

const AdminLoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const tenantId = getTenantId() ?? "";

  const fetchTokenOnce = async () => {
    const existingToken = localStorage.getItem("zulipToken");
    const existingUser = localStorage.getItem("zulipUserId");

    if (existingToken && existingUser) {
      navigate("/mm" + MMNavigationRoutes.DASHBOARD);
    } // Already fetched

    try {
      const res = await ZulipService.getZulipToken();
      localStorage.setItem("zulipToken", res.basic_token);
      localStorage.setItem("zulipUserId", res.zulip_user_id);
    } catch (err) {
      console.error("Zulip Token fetch failed:", err);
    }
  };

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      console.log("Form values:", values);
      console.log("Tenant ID:", tenantId);

      const response = await login(values.email, values.password);
      if (response) {
        message.success("Login successful!");
        await fetchTokenOnce(); // fetch Zulip token after login
        navigate("/mm" + MMNavigationRoutes.DASHBOARD);
      } else {
        message.error("Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      message.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
    message.error("Please fill in all required fields correctly.");
  };

  useEffect(() => {
    const zulipToken = localStorage.getItem("zulipToken");
    const zulipUserId = localStorage.getItem("zulipUserId");

    if (zulipToken && zulipUserId && isAuthenticated) {
      navigate("/mm" + MMNavigationRoutes.DASHBOARD);
    }
  }, [isAuthenticated, navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        // background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Row
        gutter={[0, 0]}
        style={{
          width: "100%",
          maxWidth: "1200px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Left Side - Sign Up Form */}
        <Col xs={24} lg={12}>
          <Card
            style={{
              height: "100%",
              borderRadius: "16px",
              border: "none",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            bodyStyle={{ padding: "48px" }}
          >
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <Title
                level={2}
                style={{
                  color: "#635DE8",
                  marginBottom: "8px",
                }}
              >
                Admin Login
              </Title>
            </div>

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                label={
                  <span
                    style={{
                      color: "#66697C",
                      fontWeight: "500",
                    }}
                  >
                    Email
                  </span>
                }
                rules={[
                  { required: true, message: "Please input your email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
              >
                <Input
                  className="border-[#E8E8FF] p-3 font-sans text-sm font-medium placeholder:!text-[#A7A7B7]"
                  placeholder="Email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={
                  <span
                    style={{
                      color: "#66697C",
                      fontWeight: "500",
                    }}
                  >
                    Password
                  </span>
                }
                rules={[
                  { required: true, message: "Please input your password!" },
                ]}
              >
                <Input.Password
                  className="border-[#E8E8FF] p-3 font-sans text-sm font-medium placeholder:!text-[#A7A7B7]"
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: "24px" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  disabled={loading}
                  style={{
                    height: "48px",
                    background: "#635DE8",
                    borderColor: "#635DE8",
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <Text
                style={{
                  color: "#66697C",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "1.4",
                }}
              >
                Enter your admin credentials to access the dashboard.
              </Text>
            </div>

            <div style={{ textAlign: "center" }}>
              <Text
                style={{
                  color: "#66697C",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Don&apos;t have an account?{" "}
                <Text
                  style={{
                    color: "#635DE8",
                    cursor: "pointer",
                    fontWeight: "600",
                    textDecoration: "underline",
                  }}
                  onClick={() => navigate("/" + navigationRoutes.ADMIN_SIGNUP)}
                >
                  Sign Up
                </Text>
              </Text>
            </div>
          </Card>
        </Col>

        {/* Right Side - Promotional Content */}
        <Col xs={24} lg={12}>
          <div
            style={{
              height: "100%",
              minHeight: "600px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "60px 48px",
              position: "relative",
              overflow: "hidden",
              backgroundImage: `url(${signup})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            {/* Dark Overlay for better text readability */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.4)",
                zIndex: 1,
              }}
            />

            {/* Geometric Shapes Background */}
            <div
              style={{
                position: "absolute",
                top: "10%",
                right: "10%",
                width: "120px",
                height: "120px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "20px",
                transform: "rotate(45deg)",
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "20%",
                left: "5%",
                width: "100px",
                height: "100px",
                background: "rgba(255, 255, 255, 0.08)",
                borderRadius: "50%",
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "30%",
                left: "15%",
                width: "80px",
                height: "80px",
                background: "rgba(255, 255, 255, 0.06)",
                borderRadius: "12px",
                transform: "rotate(-30deg)",
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "60%",
                right: "20%",
                width: "60px",
                height: "60px",
                background: "rgba(255, 255, 255, 0.05)",
                borderRadius: "8px",
                transform: "rotate(60deg)",
                zIndex: 2,
              }}
            />

            {/* Content */}
            <div
              style={{
                position: "relative",
                zIndex: 3,
                textAlign: "center",
                color: "white",
                width: "100%",
                maxWidth: "500px",
              }}
            >
              {/* Main Title */}
              <Title
                level={1}
                style={{
                  color: "white",
                  marginBottom: "24px",
                  fontSize: "2.8rem",
                  fontWeight: "700",
                  lineHeight: "1.2",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                }}
              >
                Welcome Back Admin
              </Title>

              {/* Description */}
              <Paragraph
                style={{
                  color: "rgba(255, 255, 255, 0.95)",
                  fontSize: "16px",
                  marginBottom: "40px",
                  lineHeight: "1.6",
                  maxWidth: "450px",
                  margin: "0 auto 40px auto",
                  textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
                }}
              >
                Access your admin dashboard to manage your organization and
                users. Secure login with your admin credentials.
              </Paragraph>

              {/* Feature List */}
              <div
                style={{
                  textAlign: "left",
                  maxWidth: "450px",
                  margin: "0 auto",
                }}
              >
                <ul
                  style={{
                    color: "white",
                    fontSize: "16px",
                    lineHeight: "1.8",
                    paddingLeft: "0",
                    listStyle: "none",
                  }}
                >
                  <li
                    style={{
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "flex-start",
                      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    <span
                      style={{
                        color: "#60a5fa",
                        marginRight: "12px",
                        fontSize: "18px",
                        fontWeight: "bold",
                        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.7)",
                      }}
                    >
                      âœ“
                    </span>
                    Full admin access to all modules and features.
                  </li>
                  <li
                    style={{
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "flex-start",
                      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    <span
                      style={{
                        color: "#60a5fa",
                        marginRight: "12px",
                        fontSize: "18px",
                        fontWeight: "bold",
                        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.7)",
                      }}
                    >
                      âœ“
                    </span>
                    Manage users, organizations, and system settings.
                  </li>
                  <li
                    style={{
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "flex-start",
                      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    <span
                      style={{
                        color: "#60a5fa",
                        marginRight: "12px",
                        fontSize: "18px",
                        fontWeight: "bold",
                        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.7)",
                      }}
                    >
                      âœ“
                    </span>
                    Secure authentication and role-based access control.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AdminLoginPage;
